"use client";

import HowDoesItWork from "@/app/(public)/homepage/sections/HowDoesItWork";
import TREATMENTS from "@/constants/treatments";
import Link from "next/link";

import KeyBenefits from "../components/KeyBenefits";
import PatientTestimonials from "../components/PatientTestimonials";
import Pricing from "../components/Pricing";
import ResearchStudies from "../components/Research";
import RightForMe from "../components/RightForMe";
import SafetySection from "../components/Safety";
import TopSection from "../components/TopSection";
import Arrow from "../components/svg/Arrow";
import Heart from "../components/svg/Heart";
import Neuron from "../components/svg/Neuron";
import { Benefit } from "../types/Benefit";
import ResearchStudiesProps from "../types/Research";
import RightForMeProps from "../types/RightForMe";

const Content = () => {
    const treatment = TREATMENTS.find(
        (treatment) => treatment.name === "Low Dose Naltrexone",
    );

    if (!treatment) {
        return null;
    }
    const benefits: Benefit[] = [
        {
            title: "Boost Energy and Reduce PEM",
            description: [
                {
                    text: "LDN acts as an immune modulator, shown to improve energy and mood, reduce inflammation, and reduce ",
                    type: "normal",
                },
                {
                    text: "post-exertional malaise (PEM)",
                    type: "normal",
                    // url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11028858/",
                },
                { text: ".", type: "normal" },
            ],
            icon: Neuron, // Replace with appropriate icon
            bgColor: "bg-[#fff9fd]",
        },
        {
            title: "Pain Relief",
            description: [
                {
                    text: "LDN stimulates the release of endorphins and enkephalins, offering a natural ",
                    type: "normal",
                },
                {
                    text: "path to relief for muscle and joint pain",
                    type: "normal",
                    // url: "https://pubmed.ncbi.nlm.nih.gov/38267326/",
                },
                {
                    text: " commonly experienced by Long COVID and ME/CFS patients.",
                    type: "normal",
                },
            ],
            icon: Arrow, // Replace with appropriate icon
            bgColor: "bg-[#fff2e4]",
        },
        {
            title: "Improve Sleep",
            description: [
                {
                    text: "LDN has been shown to reduce ",
                    type: "normal",
                },
                {
                    text: "unrefreshing sleep and abnormal sleep patterns",
                    type: "normal",
                    // url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11028858/",
                },
                {
                    text: ", allowing a better night’s rest.",
                    type: "normal",
                },
            ],
            icon: Heart, // Replace with appropriate icon
            bgColor: "bg-[#fae9e4]",
        },
        {
            title: "Clearer Cognition",
            description: [
                {
                    text: "By reducing neuroinflammation, LDN protects the brain and ",
                    type: "normal",
                },
                {
                    text: "supports clearer thinking",
                    type: "normal",
                    // url: "https://pubmed.ncbi.nlm.nih.gov/35814187/",
                },
                {
                    text: " helping you focus on what matters most.",
                    type: "normal",
                },
            ],
            icon: Neuron, // Replace with appropriate icon
            bgColor: "bg-[#f9f5f4]",
        },
    ];

    const rightForMeData: RightForMeProps = {
        treatmentName: "LDN",
        accordionData: [
            {
                title: "What Symptoms Does It Help With?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>LDN has been shown to help with:</p>
                        <ul className="space-y-1">
                            <li>Fatigue and low energy</li>
                            <li>Brain fog and impaired cognition</li>
                            <li>Chronic pain and inflammation</li>
                            <li>Post-exertional malaise</li>
                            <li>GI issues such as diarrhea, bloating, etc.</li>
                            <li>
                                Skin conditions such as rashes, hives, and dry
                                skin
                            </li>
                        </ul>
                        <p>
                            Patients with Long COVID, ME/CFS, and other chronic
                            conditions who experience these symptoms may benefit
                            from LDN.
                        </p>
                    </div>
                ),
            },
            {
                title: "Is It Safe?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>
                            LDN is generally well-tolerated. Studies in Long
                            COVID and ME/CFS suggest it is safe, with mild side
                            effects such as vivid dreams, mild headaches, or
                            occasional insomnia, which typically subside after
                            1-2 weeks of use.
                        </p>
                    </div>
                ),
            },
            {
                title: "How Do I Prevent Side Effects?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <p>
                            Starting with a low dose and gradually increasing
                            over time can reduce the risk of side effects.
                            Taking LDN in the evening may also help minimize
                            daytime discomfort.
                        </p>
                    </div>
                ),
            },
            {
                title: "Who Should Not Take LDN?",
                content: (
                    <div className="flex flex-col gap-3 opacity-80">
                        <ul className="space-y-1">
                            <li>
                                Individuals with known allergies to naltrexone
                                or its ingredients
                            </li>
                            <li>
                                Patients currently using opioids, as LDN may
                                block their effects
                            </li>
                            <li>
                                Patients who drink alcohol on a regular basis
                            </li>
                            <li>
                                Patients with kidney, liver, or heart disease
                            </li>
                        </ul>
                    </div>
                ),
            },
        ],
    };

    const researchData: ResearchStudiesProps = {
        headline: "What Does the Research Say?",
        treatmentName: "LDN",
        studies: [
            {
                title: "Study #1: LDN improved Fatigue, PEM, and Sleep Issues in 54% of Long COVID Patients",
                content: (
                    <div className="opacity-80">
                        <p>
                            A study from Stanford found that 54.2% of 59 Long
                            COVID patients using LDN experienced an improvement
                            in symptoms like fatigue, post-exertional malaise,
                            unrefreshing sleep, and abnormal sleep patterns.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Low-dose naltrexone use for the management of
                            post-acute sequelae of COVID-19”
                        </p>
                        <div className="flex gap-4 items-center">
                            <Link
                                href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11028858/"
                                target="_blank"
                                className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                            >
                                View
                            </Link>
                        </div>
                    </div>
                ),
            },
            {
                title: "Study #2: LDN Found Safe for Most Long COVID Patients",
                content: (
                    <div className="opacity-80">
                        <p>
                            52 patients with Long COVID were prescribed LDN and
                            monitored for adverse effects. 70% of patients
                            reported improvements in at least one category of
                            energy, pain levels, concentration, sleep
                            disturbances, ability to perform daily tasks, and
                            recovery from COVID-19. The study also concluded
                            that LDN was safe, with only 5.3% of participants
                            stopping due to side effects.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            ”Safety and efficacy of low dose naltrexone in a
                            long COVID cohort; an interventional pre-post study”
                        </p>
                        <div className="flex gap-4 items-center">
                            <Link
                                href="https://pubmed.ncbi.nlm.nih.gov/35814187/"
                                target="_blank"
                                className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                            >
                                View
                            </Link>
                        </div>
                    </div>
                ),
            },
            {
                title: "Study #3: LDN in combination with NAD+ Increased Long COVID Patient Energy",
                content: (
                    <div className="opacity-80">
                        <p>
                            36 Long COVID patients were given LDN and NAD+
                            patches for 12 weeks. The treatment boosted energy
                            and reduced fatigue for approximately 50% of
                            patients.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Low-dose naltrexone and NAD+ for the treatment of
                            patients with persistent fatigue symptoms after
                            COVID-19”
                        </p>
                        <div className="flex gap-4 items-center">
                            <Link
                                href="https://pubmed.ncbi.nlm.nih.gov/38352659/"
                                target="_blank"
                                className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                            >
                                View
                            </Link>
                        </div>
                    </div>
                ),
            },
            {
                title: "Study #4: LDN increased the likelihood of reduced fatigue & pain by 5x against controls",
                content: (
                    <div className="opacity-80">
                        <p>
                            A study of 108 patients taking amitriptyline,
                            low-dose naltrexone, and physical therapy found that
                            those taking LDN had a 5 times higher likelihood of
                            improvement for both fatigue and pain.
                        </p>
                        <p className="text-[#333333] text-lg font-medium font-satoshi leading-relaxed dark:text-gray-400">
                            “Low-dose naltrexone and NAD+ for the treatment of
                            patients with persistent fatigue symptoms after
                            COVID-19”
                        </p>
                        <div className="flex gap-4 items-center">
                            <Link
                                href="https://pubmed.ncbi.nlm.nih.gov/38267326/"
                                target="_blank"
                                className="text-[#1a5b77] text-lg font-light font-space underline dark:text-blue-300"
                            >
                                View
                            </Link>
                        </div>
                    </div>
                ),
            },
        ],
    };

    const testimonialsData = {
        headline:
            "What People Are Saying About Their Experience with Low Dose Naltrexone",
        testimonials: [
            {
                quote: "57% of patients reported improvements on LDN.",
                source: "stuffthatworks.health",
                sourceLink:
                    "https://www.stuffthatworks.health/myalgic-encephalomyelitis/treatments/naltrexone-hydrochloride",
            },
            {
                quote: "It has without a doubt helped give me a significant bump in my baseline. I've been on it for several months now. I'm at 2mg and plan to bump up soon. As others mentioned, it does give you vivid dreams at times. Otherwise, I don't notice any other side effects. I've been able to start hiking again without any PEM.",
                author: "u/Responsible-Heat6842",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/covidlonghaulers/comments/1eq340h/whats_your_experience_with_low_dose_naltrexone/",
            },
            {
                quote: "...joined a triple therapy trial. <The triple therapy got me to> a 6-7/10 <but> my blood work wasn't changing. I spoke to my other doctor about LDN and gave that a 6 month trial. That slowly got me back to a 10/10.",
                author: "u/KentuckyFriedSoy",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/covidlonghaulers/comments/1g3cqgx/i_never_thought_this_day_would_come/",
            },
            {
                quote: "I am pretty much back to normal ... ldn helped me so much!! I do everything I used to do, however just a little bit more easy to tire out! A very far cry from when I was bed bound for 3 months. I owe my life to ldn!",
                author: "u/Good_Pie2522",
                source: "reddit.com",
                sourceLink:
                    "https://www.reddit.com/r/cfs/comments/105l9he/9_weeks_on_ldn_update_i_mostly_have_my_life_back/",
            },
        ],
    };

    return (
        <div className="w-full h-full flex flex-col gap-16">
            <TopSection
                treatmentName={treatment.name}
                treatmentId={treatment.id}
                title="Boost your energy, reduce pain, and clear brain fog"
                subtitle="Get a safe and clinician approved prescription for LDN."
            />
            <HowDoesItWork />
            <Pricing treatment={treatment} />
            <KeyBenefits benefits={benefits} />
            <RightForMe {...rightForMeData} />
            <ResearchStudies {...researchData} />
            <PatientTestimonials {...testimonialsData} />
            <SafetySection treatmentName={treatment.name} />
        </div>
    );
};

export default Content;
